import {min} from 'lodash-es'
import LocalDate from '@booking-services/localDate'
import roundMoney from 'root-packages/price-engine/roundMoney.js'
import earliestPromoPaymentDate from 'root-packages/promotions/earliestPromoPaymentDate.js'

function getPaymentAmount ({
  product: {price, code, flights},
  paymentPolicy: {amount, amountType},
  numberOfPassengers,
  depositAmount = 0,
}) {
  let fuelSurcharge = code === 'AIRPROD' && flights ? flights.breakdown.fuelSurcharge : 0

  let amountBasedOnPolicy = amountType === 'fixed'
    ? numberOfPassengers * amount
    : amount * (price + fuelSurcharge) / 100

  return roundMoney(Math.min(price - depositAmount, amountBasedOnPolicy))
}


export default class PaymentSchedulePolicy {
  constructor ({
    paymentschedulepolicyid,
    sellingCompanyCode = null,
    bookingType = null,
    taLevel = null,
    travelAgencyId = null,
    productType = null,
    productCode = null,
    productCategory = null,
    productCategoryId = null,
    productClassificationId = null,
    operatingProductCode = null,
    brochureCode = null,
    combinePaymentDays = 0,
    scope = {},
    product = {},
    deposit,
    finalPayment,
    intermediatePayment,
  }) {
    if (!sellingCompanyCode && !scope.sellingCompany) {
      throw new Error(`Selling company must be provided`)
    }

    if (!bookingType && !scope.bookingType) {
      throw new Error(`Booking type must be provided`)
    }

    this.paymentschedulepolicyid = paymentschedulepolicyid

    this.scope = Object.assign({}, {
      sellingCompany: sellingCompanyCode,  // [cs_sc_id]
      bookingType, // [rs_rs_paymschpolicybkgtype.bookingtypecode]
      taLevel, // [talevelcode]
      travelAgencyId, // [rs_ta_id]
      // [rs_group_id] // not currently used for MVP1
      // [rs_consr_id] // not currently used for MVP1
    }, scope)

    this.product = {
      productType, // [producttypecode]
      productCode, // [pr_pm_id]
      productCategory, // [productcategorycode]
      productCategoryId, // [cs_gmd_prdcategory_id]
      productClassificationId, // [cs_ocgm_prdclassification_id]
      operatingProductCode, // [pr_opd_id]
      brochureCode, // [pr_bro_id]
      ...product,
    },

    this.deposit = deposit || {
      amount: 0, // [deposit]
      amountType: 'fixed', // [deposittypecode]
      numberOfDays: 0, // [noofdays]
      afterBookingOrBeforeDeparture: true, // [afterbkgbeforedeptflag]
      cabinClasses: [
        // {
        //   amount: 0, // [cabinclassdeposit]
        //   amountType: 'fixed', // [cabinclassdeposittype]
        //   typeCode: 'Economy', [cabinclasstype]
        // }
      ],
    }

    this.finalPayment = finalPayment || {
      daysBeforeDeparture: 60, // [finalpaymentday]
      dueDate: null, // [paymentfinaldate]
    }

    this.combinePaymentDays = combinePaymentDays

    /* Intermediate payment properties - not needed for MVP1 iteration
    {
      amount {Integer} // [inventoryamount]
      amountType {String} // [inventoryamounttypecode]
      afterBkgOrBeforeDept {String:Y|N|T} // [invafterbkgorbeforedepflag]
      noOfDays {Integer} // [inventorynumberofdays]
      amountBelow {Integer=0} // [inventoryamountbelow]
      dueDate {Date|null} // [paymentintermediatedate]
      amount {Integer=0} //  [productamount]
      amountType {String|null=null} // [productamounttypecode]
      dueDays {Integer=0} // [productnoofdays]
    }*/
    this.intermediatePayment = intermediatePayment
  }


  paymentScheduleDeposit ({
    product,
    numberOfPassengers,
    bookingDate,
    departureDate,
    promotionsPaymentDueDate,
    appliedPromotions,
  }) {
    let cabinClassDepositPolicy
    const {deposit} = this

    if (product.code === 'AIRPROD' && deposit.cabinClasses.length > 0) {
      // base the deposit amount on the cabin class deposit policy, if specified in the
      // payment schedule policy, using the highest class leg in the flights itinerary
      let classPreference = (product.flights.itinerary || [])
        .reduce((highestClass, {cabin}) =>
          ['First', 'Business', 'Premium Economy', 'Economy']
            .find(className => highestClass === className || cabin === className),
        'Economy'
        )
      cabinClassDepositPolicy = deposit.cabinClasses.find(({typeCode, amount}) =>
        typeCode === classPreference && amount > 0)
    }

    const promoDepositAmount = appliedPromotions.reduce((depositAmount, promo) => {
      if (typeof promo.depositAmount === 'number') {
        const currentPromoDepositAmount = getPaymentAmount({
          product,
          paymentPolicy: {
            amount: promo.depositAmount,
            amountType: promo.depositPercentFlag === false ? 'fixed' : 'percentage',
          },
          numberOfPassengers,
        })
        return Math.max(currentPromoDepositAmount, depositAmount)
      }
      return depositAmount
    }, null)

    const paymentAmount = typeof promoDepositAmount === 'number'
      ? promoDepositAmount
      : getPaymentAmount({
        product,
        paymentPolicy: cabinClassDepositPolicy || deposit,
        numberOfPassengers,
      })

    if (paymentAmount <= 0) return

    let paymentDueDate = min([
      deposit.afterBookingOrBeforeDeparture
        ? bookingDate.addDays(deposit.numberOfDays)
        : departureDate.addDays(0 - deposit.numberOfDays),
      promotionsPaymentDueDate,
    ])

    if (paymentDueDate < bookingDate || paymentDueDate >= departureDate) {
      paymentDueDate = bookingDate
    }

    const returnDeposit = {
      paymentType: 'Deposit',
      paymentAmount,
      paymentDueDate,
      products: [product.name],
      combinePaymentDays: this.combinePaymentDays,
    }

    return returnDeposit
  }

  paymentScheduleIntermediate ({
    product,
    numberOfPassengers,
    bookingDate,
    departureDate,
    earliestFinalPaymentDateAllProducts,
    depositAmount = 0,
  }) {
    if (!this.intermediatePayment) return
    const paymentAmount = getPaymentAmount({
      product,
      paymentPolicy: this.intermediatePayment,
      numberOfPassengers,
      depositAmount,
    })

    if (paymentAmount <= 0) return

    let paymentDueDate = min([
      this.intermediatePayment.afterBookingOrBeforeDeparture
        ? bookingDate.addDays(this.intermediatePayment.numberOfDays)
        : departureDate.addDays(0 - this.intermediatePayment.numberOfDays),
      earliestFinalPaymentDateAllProducts,
      // not needed if we don't support absolute payment dates on intermediate payments
      // dueDate: this.intermediatePayment.dueDate,
    ])

    if (paymentDueDate < bookingDate || paymentDueDate >= departureDate) {
      paymentDueDate = bookingDate
    }

    return {
      paymentType: 'Intermediate Payment',
      paymentAmount,
      paymentDueDate,
      products: [product.name],
      combinePaymentDays: this.combinePaymentDays,
    }
  }


  paymentScheduleFinal ({
    product,
    bookingDate,
    departureDate,
    promotionsPaymentDueDate,
    earliestFinalPaymentDateAllProducts,
    epdPaymentDueDate,
    priorPaymentsTotal = 0,
  }) {
    const paymentAmount = roundMoney(product.price - priorPaymentsTotal)

    if (paymentAmount <= 0) return

    const {
      combinePaymentDays,
      finalPayment: {
        daysBeforeDeparture,
        dueDate,
      },
    } = this

    if (!daysBeforeDeparture && !dueDate) {
      return
    }

    let policyPaymentDueDate = dueDate || departureDate.addDays(-daysBeforeDeparture)

    let ttlDate

    if (product.code === 'AIRPROD' && product.flights.fares) {
      ttlDate = min(product.flights.fares.map(({ttlDate}) => new LocalDate(ttlDate))).addDays(-7)
    }

    let paymentDueDate = min([
      policyPaymentDueDate,
      promotionsPaymentDueDate,
      earliestFinalPaymentDateAllProducts,
      epdPaymentDueDate,
      ttlDate,
    ].filter(Boolean))

    if (paymentDueDate < bookingDate || paymentDueDate >= departureDate) {
      paymentDueDate = bookingDate
    }

    const paymentFinal = {
      paymentType: 'Final Payment',
      paymentAmount,
      paymentDueDate,
      products: [product.name],
      combinePaymentDays,
    }

    return paymentFinal
  }

  paymentSchedule ({
    product,
    numberOfPassengers,
    bookingDate,
    departureDate,
    appliedPromotions,
  }) {
    const getEarliestPromotionPaymentDate = (paymentType, onlyPromosAppliedToAllProducts = false) => {
      const paymentByProp = paymentType === 'deposit' ? 'depositPaymentBy' : 'paymentBy'
      return min(
        appliedPromotions
          .filter(({[paymentByProp]: paymentBy, code}) =>
            code !== 'EPD' &&
            paymentBy &&
            (
              paymentType !== 'finalPayment' ||
              !onlyPromosAppliedToAllProducts ||
              paymentBy.applyToAllProducts
            )
          )
          .map(promo => earliestPromoPaymentDate(
            promo[paymentByProp],
            {startDate: departureDate},
            bookingDate
          ))
      )
    }

    const deposit = this.paymentScheduleDeposit({
      product,
      numberOfPassengers,
      bookingDate,
      departureDate,
      promotionsPaymentDueDate: getEarliestPromotionPaymentDate('deposit'),
      appliedPromotions,
    })

    const earliestFinalPaymentDateAllProducts = getEarliestPromotionPaymentDate('finalPayment', true)

    const intermediate = this.paymentScheduleIntermediate({
      product,
      numberOfPassengers,
      bookingDate,
      departureDate,
      earliestFinalPaymentDateAllProducts,
      depositAmount: deposit ? deposit.paymentAmount : 0,
    })

    const epd = appliedPromotions.find(promo => promo.code === 'EPD')

    const final = this.paymentScheduleFinal({
      product,
      bookingDate,
      departureDate,
      promotionsPaymentDueDate: getEarliestPromotionPaymentDate('finalPayment'),
      earliestFinalPaymentDateAllProducts,
      epdPaymentDueDate: epd && epd.paymentBy ? epd.paymentBy.date : undefined,
      priorPaymentsTotal: (deposit ? deposit.paymentAmount : 0) +
        (intermediate ? intermediate.paymentAmount : 0),
    })

    if (deposit && final && deposit.paymentDueDate > final.paymentDueDate) {
      deposit.paymentDueDate = final.paymentDueDate
    }

    const paymentSchedule = [deposit, intermediate, final].filter(Boolean)
    return paymentSchedule
  }
}
